import { FC, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  removeJobValidationErrors,
  selectAutoFilledData,
  selectIsJobScheduled,
  selectJobValidationErrors,
  selectScheduledFrom,
  selectScheduledTo,
  selectSelectedDate,
  toggleIsJobScheduled,
  updateScheduledFrom,
  updateScheduledTo,
  updateSelectedDate
} from '../createJobSlice';
import { DateTimeScheduler, RadioWithLabel } from 'components';
import { CreateJobErrorTypes } from 'containers/jobs/constant';
import { getJobValidationErrors } from 'containers/jobs/utils';
import AutoFilledIndicator from './AutoFilledIndicator';

interface ScheduleSectionProps {
  className?: string;
  isEdit: boolean;
}

enum ScheduleType {
  SCHEDULE_FOR = 'Schedule for',
  SCHEDULE_LATER = 'Schedule later'
}

const ScheduleSection: FC<ScheduleSectionProps> = ({ isEdit }) => {
  const [startTimeError, setStartTimeError] = useState('');
  const [endTimeError, setEndTimeError] = useState('');

  const selectedDate = useAppSelector(selectSelectedDate);
  const startTime = useAppSelector(selectScheduledFrom);
  const endTime = useAppSelector(selectScheduledTo);
  const isJobScheduled = useAppSelector(selectIsJobScheduled);
  const autoFilledData = useAppSelector(selectAutoFilledData);
  const setStartTime = (date: Date | null) => {
    dispatch(removeJobValidationErrors(CreateJobErrorTypes.SCHEDULE_DATE));
    dispatch(updateScheduledFrom(date?.toISOString()));
  };

  const setEndTime = (date: Date | null) => {
    dispatch(removeJobValidationErrors(CreateJobErrorTypes.SCHEDULE_DATE));
    dispatch(updateScheduledTo(date?.toISOString()));
  };

  const setSelectedDate = (date: Date | null) => {
    dispatch(updateSelectedDate(date?.toDateString()));
  };

  const dispatch = useAppDispatch();
  const selectedJobValidations = useAppSelector(selectJobValidationErrors);

  const updateJobScheduleFor = () => {
    dispatch(removeJobValidationErrors(CreateJobErrorTypes.SCHEDULE_DATE));
    dispatch(toggleIsJobScheduled(true));
  };

  const updateJobScheduleLater = () => {
    dispatch(removeJobValidationErrors(CreateJobErrorTypes.SCHEDULE_DATE));
    dispatch(toggleIsJobScheduled(false));
  };

  const jobScheduleError = getJobValidationErrors(selectedJobValidations, CreateJobErrorTypes.SCHEDULE_DATE);

  const isAutoFilledIndicate = () => {
    if (autoFilledData?.isScheduled) {
      return (
        new Date(autoFilledData?.scheduledFrom).toISOString() === new Date(startTime).toISOString() &&
        new Date(autoFilledData?.scheduledTo).toISOString() === new Date(endTime).toISOString()
      );
    }

    return false;
  };

  return (
    <div>
      <div className="mb-2 flex text-17px font-semibold text-heading">
        Schedule<span className="text-error">*</span>
        <AutoFilledIndicator isAutoFilled={isAutoFilledIndicate()} />
      </div>
      {jobScheduleError && !startTimeError && !endTimeError && (
        <div className="mb-2 text-sbase font-semibold text-error">{jobScheduleError}</div>
      )}
      <div className="rounded-md border border-borderGray">
        <div className="flex rounded-t-md bg-[#F4F4F4]">
          <div className={`rounded-tl-md py-3.5 pr-12 pl-4 ${isJobScheduled ? 'bg-primary' : ''}`}>
            <RadioWithLabel
              label={ScheduleType.SCHEDULE_FOR}
              onClick={updateJobScheduleFor}
              isSelected={isJobScheduled}
            />
          </div>
          <div className={`py-3.5 pr-12 pl-4 ${!isJobScheduled ? 'bg-primary' : ''}`}>
            <RadioWithLabel
              label={ScheduleType.SCHEDULE_LATER}
              onClick={updateJobScheduleLater}
              isSelected={!isJobScheduled}
            />
          </div>
        </div>
        <div className={`rounded-b-md bg-primary p-6 ${startTimeError || endTimeError ? 'pb-10' : ''}`}>
          {!isJobScheduled && (
            <div className="text-sbase font-semibold text-accentText">
              Job will be listed as “unscheduled job“, you can schedule it later from the job details page.
            </div>
          )}

          {isJobScheduled && (
            <DateTimeScheduler
              startTimePickerId="job-start-time"
              endTimePickerId="job-end-time"
              selectedDate={new Date(selectedDate)}
              setSelectedDate={setSelectedDate}
              startTime={new Date(startTime)}
              endTime={new Date(endTime)}
              setStartTime={setStartTime}
              setEndTime={setEndTime}
              startTimeError={startTimeError}
              setStartTimeError={setStartTimeError}
              endTimeError={endTimeError}
              setEndTimeError={setEndTimeError}
              showPastDates={true}
              showDaysDifference={true}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduleSection;
